const r = (a, b) => [a, b];

export const FORMS = {
  free: { name: "Free verse", group: "Open", desc: "No set rules. Counts and rhyme letters are shown so you can shape the rhythm yourself." },
  haiku: { name: "Haiku", group: "Short", lines: 3, syl: [5, 7, 5], desc: "Three lines of 5, 7 and 5 syllables. Traditionally a moment in nature, with a turn or contrast." },
  tanka: { name: "Tanka", group: "Short", lines: 5, syl: [5, 7, 5, 7, 7], desc: "Five lines of 5-7-5-7-7. The third line often pivots from an image to a feeling." },
  shakespearean: { name: "Shakespearean sonnet", group: "Sonnets", lines: 14, syl: 10, scheme: "ABAB CDCD EFEF GG", desc: "14 lines of about 10 syllables (iambic pentameter): three quatrains and a closing couplet that turns or resolves." },
  petrarchan: { name: "Petrarchan sonnet", group: "Sonnets", lines: 14, syl: 10, scheme: "ABBAABBA CDECDE", alt: "ABBAABBA CDCDCD", desc: "An 8-line octave (ABBAABBA) poses a problem; a 6-line sestet (CDECDE or CDCDCD) answers it. About 10 syllables a line." },
  villanelle: { name: "Villanelle", group: "Sonnets", lines: 19, syl: { range: [8, 11] }, scheme: "ABA ABA ABA ABA ABA ABAA", refrains: { 1: [6, 12, 18], 3: [9, 15, 19] },
    desc: "19 lines: five tercets and a quatrain on two rhymes. Line 1 returns as lines 6, 12 and 18; line 3 as lines 9, 15 and 19." },
  limerick: { name: "Limerick", group: "Light", lines: 5, syl: [r(7, 10), r(7, 10), r(5, 7), r(5, 7), r(7, 10)], scheme: "AABBA", desc: "Five bouncy lines, AABBA. Long lines 1, 2 and 5 (about 8–9 syllables), short lines 3 and 4 (about 5–6)." },
  ballad: { name: "Ballad (common meter)", group: "Light", syl: [8, 6, 8, 6], repeat: true, scheme: "ABCB", desc: "Four-line stanzas alternating 8 and 6 syllables, usually rhyming lines 2 and 4. Tells a story." },
  song: { name: "Song (verse / chorus)", group: "Song", song: true, desc: "Mark sections on their own line, e.g. [Verse 1], [Pre-Chorus], [Chorus], [Bridge]. Matching lines in sections of the same kind are compared so verses sing to the same tune." },
};

const target = (f, i) => {
  const s = f.syl;
  if (!s) return null;
  if (typeof s === "number") return [s - 1, s + 1];
  if (s.range) return s.range;
  const t = f.repeat ? s[i % s.length] : s[i];
  if (t == null) return null;
  return Array.isArray(t) ? t : [t, t];
};

const norm = (t) => t.toLowerCase().replace(/[^a-z\s]/g, "").replace(/\s+/g, " ").trim();

const songCheck = (rows) => {
  const seen = new Map();
  let idx = 0;
  let sec = null;
  return rows.map((l) => {
    if (l.header || l.blank) { if (l.header) { sec = l.section; idx = 0; } return { ...l }; }
    const k = `${sec || "verse"}:${idx++}`;
    const first = seen.get(k);
    if (first == null) { seen.set(k, l.syl); return { ...l }; }
    return { ...l, target: [first - 1, first + 1], ok: Math.abs(l.syl - first) <= 1, why: `to match line ${idx} of the first ${sec || "verse"}, which has ${first}` };
  });
};

export const checkForm = (key, rows) => {
  const f = FORMS[key] || FORMS.free;
  if (f.song) return { rows: songCheck(rows), notes: [] };
  const body = rows.filter((l) => !l.blank && !l.header);
  const scheme = (f.scheme || "").replace(/\s/g, "");
  const alt = (f.alt || "").replace(/\s/g, "");
  const notes = [];
  if (f.lines && body.length !== f.lines) notes.push(body.length < f.lines ? `${f.lines - body.length} more line${f.lines - body.length === 1 ? "" : "s"} to go (${f.lines} in all).` : `${body.length - f.lines} line${body.length - f.lines === 1 ? "" : "s"} over the ${f.lines} this form uses.`);
  const map = new Map();
  const want = (i) => (scheme ? (f.repeat ? scheme[i % scheme.length] : scheme[i]) : null);
  let bi = 0;
  let schemeOk = true;
  const out = rows.map((l) => {
    if (l.blank || l.header) return { ...l };
    const i = bi++;
    const t = target(f, i);
    const exp = want(i);
    let letterOk = true;
    if (exp) {
      const groupKey = f.repeat ? `${Math.floor(i / scheme.length)}${exp}` : exp;
      if (!map.has(groupKey)) map.set(groupKey, { letter: l.letter, line: i + 1 });
      else { const g = map.get(groupKey); letterOk = g.letter === l.letter; if (!letterOk) { schemeOk = false; return { ...l, target: t, ok: t ? l.syl >= t[0] && l.syl <= t[1] : true, expected: exp, letterOk, rhymeWith: g.line }; } }
    }
    return { ...l, target: t, ok: t ? l.syl >= t[0] && l.syl <= t[1] : true, expected: exp, letterOk };
  });
  if (alt && !schemeOk) {
    const got = body.map((l) => l.letter).join("");
    if (got.length === alt.length) notes.push("Tip: the sestet may also follow CDCDCD.");
  }
  if (f.refrains) {
    Object.entries(f.refrains).forEach(([src, dests]) => {
      const a = body[src - 1];
      dests.forEach((d) => { const b = body[d - 1]; if (a && b && norm(a.text) !== norm(b.text)) notes.push(`Line ${d} usually repeats line ${src}.`); });
    });
  }
  if (scheme && !schemeOk) notes.push(`Rhyme pattern: ${f.scheme}. Lines marked in red don't rhyme where the pattern expects.`);
  return { rows: out, notes };
};
